import type { Owner } from '@/modules/task/domain/owner/Owner';
import type { OwnerRepository } from '@/modules/task/domain/owner/OwnerRepository';
import type { TimelineEvent } from '@/modules/task/domain/timeline/TimelineEvent';
import { OwnerId } from '@/modules/task/domain/owner/OwnerId';
import { IsoDate } from '@/modules/task/domain/time/IsoDate';
import type { TimezoneRepository } from '@/modules/task/domain/time/TimezoneRepository';
import type { Message } from '@/modules/task/domain/message/Message';
import type { MediaType } from '@/modules/task/domain/message/MediaTypes';

export class WhatsAppMessage implements TimelineEvent, Message {
  constructor(
    readonly id: string,
    readonly status: 'sent' | 'delivered' | 'read' | 'failed',
    readonly direction: 'inbound' | 'outbound',
    readonly createdAt: string,
    readonly body = '',
    readonly ownerId: OwnerId = OwnerId.empty,
    readonly mediaType: MediaType | null = null,
  ) {}

  get isInbound(): boolean {
    return this.direction === 'inbound';
  }

  get isOutbound(): boolean {
    return this.direction === 'outbound';
  }

  get isFailed(): boolean {
    return this.status === 'failed';
  }

  get hasOwner(): boolean {
    return !this.ownerId.isEmpty;
  }

  get hasMedia(): boolean {
    return !!this.mediaType;
  }

  get hasBody(): boolean {
    return this.body.trim() !== '';
  }

  get isValid(): boolean {
    return !!this.body.length || this.hasMedia;
  }

  get isSentBySystem(): boolean {
    return this.ownerId.isEmpty && this.isOutbound;
  }

  get isSuccessfulOutbound(): boolean {
    return this.isOutbound && !this.isSentBySystem && !this.isFailed;
  }

  get isSuccessfulInbound(): boolean {
    return this.isInbound;
  }

  displayedDate(timezoneRepository: TimezoneRepository): IsoDate {
    return this.createdAt !== ''
      ? IsoDate.make(this.createdAt, timezoneRepository.get())
      : IsoDate.empty;
  }

  isMine(ownerRepository: OwnerRepository): boolean {
    return this.isOwnedBy(ownerRepository.getMe());
  }

  isOwnedBy(owner: Owner): boolean {
    return owner.id.isEqual(this.ownerId);
  }
}
